import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Package, AlertTriangle, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, ShoppingCart, Search, Eye } from 'lucide-react';
import api from '../../services/api';

const InventoryReports = () => {
  const [activeReport, setActiveReport] = useState('items');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState({});
  const [posTransactions, setPosTransactions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const filterByDate = (date) => {
    if (!date) return true;
    const d = new Date(date).toISOString().split('T')[0];
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('/inventory/items', { params: { limit: 500 } }),
        api.get('/inventory/movements', { params: { limit: 500 } }),
        api.get('/inventory/dashboard/stats'),
        api.get('/pos/transactions', { params: { limit: 100 } })
      ]);
      if (results[0].status === 'fulfilled') setItems(results[0].value.data?.data?.items || []);
      if (results[1].status === 'fulfilled') setMovements(results[1].value.data?.data?.movements || []);
      if (results[2].status === 'fulfilled') setStats(results[2].value.data?.data || {});
      if (results[3].status === 'fulfilled') setPosTransactions(results[3].value.data?.data?.transactions || []);
    } catch (err) { console.error('Error:', err); }
    setLoading(false);
  };

  // Calculations
  const totalItems = items.length;
  const totalValue = items.reduce((sum, i) => sum + (parseFloat(i.current_stock) * parseFloat(i.unit_cost || 0)), 0);
  const lowStockItems = items.filter(i => parseFloat(i.current_stock) <= parseFloat(i.minimum_stock));
  const outOfStock = items.filter(i => parseFloat(i.current_stock) === 0);
  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
  
  // Movement summary
  const filteredMovements = movements.filter(m => filterByDate(m.movement_date));
  const filteredPosTransactions = posTransactions.filter(t => filterByDate(t.transaction_date));
  const movementSummary = {
    in: filteredMovements.filter(m => m.movement_type === 'in').reduce((s, m) => s + parseFloat(m.quantity || 0), 0),
    out: filteredMovements.filter(m => m.movement_type === 'out').reduce((s, m) => s + parseFloat(m.quantity || 0), 0)
  };
  
  // Category stats
  const categoryStats = categories.map(cat => {
    const catItems = items.filter(i => i.category === cat);
    return { name: cat, count: catItems.length, percentage: Math.round((catItems.length / totalItems) * 100) || 0 };
  }).sort((a, b) => b.count - a.count);

  // Filter items
  const filteredItems = items.filter(i => {
    if (searchTerm && !i.name.toLowerCase().includes(searchTerm.toLowerCase()) && !i.sku?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (categoryFilter && i.category !== categoryFilter) return false;
    return true;
  });

  // Get item movements
  const getItemMovements = (itemId) => movements.filter(m => m.item_id === itemId && filterByDate(m.movement_date));
  const getItemSold = (itemId) => getItemMovements(itemId).filter(m => m.movement_type === 'out').reduce((s, m) => s + parseFloat(m.quantity), 0);
  const getItemReceived = (itemId) => getItemMovements(itemId).filter(m => m.movement_type === 'in').reduce((s, m) => s + parseFloat(m.quantity), 0);

  const getStockStatus = (item) => {
    const stock = parseFloat(item.current_stock);
    const min = parseFloat(item.minimum_stock);
    if (stock === 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (stock <= min) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  const reportTabs = [
    { id: 'items', label: 'Item Report', icon: Package },
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'movements', label: 'Movements', icon: ArrowUpRight },
    { id: 'sales', label: 'Sales', icon: ShoppingCart },
    { id: 'lowStock', label: 'Low Stock', icon: AlertTriangle },
    { id: 'valuation', label: 'Valuation', icon: TrendingUp }
  ];

  const formatCurrency = (val) => `₦${parseFloat(val || 0).toLocaleString()}`;

  const exportToCSV = () => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    let rows = [];

    if (activeReport === 'items') {
      rows.push(['SKU', 'Item Name', 'Category', 'Current Stock', 'Unit Cost', 'Total Value', 'Status']);
      filteredItems.forEach(item => {
        const value = parseFloat(item.current_stock) * parseFloat(item.unit_cost || 0);
        const status = getStockStatus(item).label;
        rows.push([item.sku, item.name, item.category, item.current_stock, item.unit_cost, value.toFixed(2), status]);
      });
    } else if (activeReport === 'lowStock') {
      rows.push(['SKU', 'Item Name', 'Current Stock', 'Minimum Stock', 'Unit Cost']);
      lowStockItems.forEach(item => {
        rows.push([item.sku, item.name, item.current_stock, item.minimum_stock, item.unit_cost]);
      });
    } else if (activeReport === 'valuation') {
      rows.push(['Item Name', 'Quantity', 'Unit Cost', 'Total Value']);
      items.forEach(item => {
        const value = parseFloat(item.current_stock) * parseFloat(item.unit_cost || 0);
        rows.push([item.name, item.current_stock, item.unit_cost, value.toFixed(2)]);
      });
    }

    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `inventory_report_${activeReport}_${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
  };

  if (loading) return (
    <div className="p-6 flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Inventory Reports</h1>
          <p className="text-gray-600">Analytics and insights</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow">
            <span className="text-sm text-gray-600">From:</span>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1 border rounded text-sm" />
            <span className="text-sm text-gray-600">To:</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1 border rounded text-sm" />
          </div>
          <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
            <Download className="w-4 h-4" /> Refresh
          </button>
          <button onClick={exportToCSV} className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b flex overflow-x-auto">
          {reportTabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveReport(tab.id); setSelectedItem(null); }}
              className={`px-6 py-4 flex items-center gap-2 border-b-2 whitespace-nowrap ${activeReport === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Item Report - NEW */}
      {activeReport === 'items' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg" />
              </div>
            </div>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Item Detail Modal */}
          {selectedItem && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">{selectedItem.name}</h3>
                  <p className="text-sm text-gray-600">Item Code: {selectedItem.sku}</p>
                </div>
                <button onClick={() => setSelectedItem(null)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded">
                  <p className="text-xs text-gray-500">Current Stock</p>
                  <p className="text-xl font-bold">{parseFloat(selectedItem.current_stock)}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-xs text-gray-500">Stock Value</p>
                  <p className="text-xl font-bold">{formatCurrency(parseFloat(selectedItem.current_stock) * parseFloat(selectedItem.unit_cost || 0))}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-xs text-gray-500">Sold/Transferred</p>
                  <p className="text-xl font-bold text-red-600">-{getItemSold(selectedItem.id)}</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="text-xs text-gray-500">Received</p>
                  <p className="text-xl font-bold text-green-600">+{getItemReceived(selectedItem.id)}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStockStatus(selectedItem).color}`}>
                  {getStockStatus(selectedItem).label}
                </span>
                <span className="text-sm text-gray-600">Min: {parseFloat(selectedItem.minimum_stock)} | Reorder Qty: {parseFloat(selectedItem.reorder_quantity || 0)}</span>
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Stock</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Sold</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Value</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => {
                  const status = getStockStatus(item);
                  const value = parseFloat(item.current_stock) * parseFloat(item.unit_cost || 0);
                  return (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-xs text-gray-500">{item.sku}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">{item.category || '-'}</td>
                      <td className="px-4 py-3 text-right font-medium">{parseFloat(item.current_stock)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{getItemSold(item.id)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(value)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setSelectedItem(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredItems.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No items found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Overview Report */}
      {activeReport === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <Package className="w-10 h-10 text-blue-600" />
                <div>
                  <p className="text-gray-600 text-sm">Total Items</p>
                  <p className="text-2xl font-bold">{totalItems}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-10 h-10 text-green-600" />
                <div>
                  <p className="text-gray-600 text-sm">Total Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-10 h-10 text-yellow-600" />
                <div>
                  <p className="text-gray-600 text-sm">Low Stock</p>
                  <p className="text-2xl font-bold">{lowStockItems.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <TrendingDown className="w-10 h-10 text-red-600" />
                <div>
                  <p className="text-gray-600 text-sm">Out of Stock</p>
                  <p className="text-2xl font-bold">{outOfStock.length}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-4">Stock Movement Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                  <span className="flex items-center gap-2"><ArrowDownRight className="w-5 h-5 text-green-600" /> Stock In</span>
                  <span className="font-bold text-green-600">{movementSummary.in.toLocaleString()} units</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                  <span className="flex items-center gap-2"><ArrowUpRight className="w-5 h-5 text-red-600" /> Stock Out</span>
                  <span className="font-bold text-red-600">{movementSummary.out.toLocaleString()} units</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-4">Top Categories</h3>
              <div className="space-y-3">
                {categoryStats.slice(0, 5).map((cat, i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span>{cat.name}</span>
                    <span className="text-gray-600">{cat.count} items ({cat.percentage}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Low Stock Report */}
      {activeReport === 'lowStock' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-bold">Low Stock Items ({lowStockItems.length})</h3>
            <p className="text-sm text-gray-600">Items at or below minimum stock level</p>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                <th className="px-4 py-3 text-left text-sm font-medium">SKU</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Current</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Minimum</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Shortage</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
              </tr>
            </thead>
            <tbody>
              {lowStockItems.map(item => (
                <tr key={item.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600">{item.sku}</td>
                  <td className="px-4 py-3 text-right text-red-600 font-bold">{parseFloat(item.current_stock)}</td>
                  <td className="px-4 py-3 text-right">{parseFloat(item.minimum_stock)}</td>
                  <td className="px-4 py-3 text-right text-red-600">{parseFloat(item.minimum_stock) - parseFloat(item.current_stock)}</td>
                  <td className="px-4 py-3">{item.InventoryLocation?.name || '-'}</td>
                </tr>
              ))}
              {lowStockItems.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No low stock items</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock Valuation Report */}
      {activeReport === 'valuation' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-bold">Stock Valuation</h3>
            <p className="text-sm text-gray-600">Total inventory value: {formatCurrency(totalValue)}</p>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Item Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">SKU</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Stock</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Unit Cost</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Total Value</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const itemValue = parseFloat(item.current_stock || 0) * parseFloat(item.unit_cost || 0);
                return (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{item.sku}</td>
                    <td className="px-4 py-3 text-sm">{item.category || 'Uncategorized'}</td>
                    <td className="px-4 py-3 text-right">{parseFloat(item.current_stock || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(item.unit_cost)}</td>
                    <td className="px-4 py-3 text-right font-bold">{formatCurrency(itemValue)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-100 font-bold">
              <tr>
                <td className="px-4 py-3" colSpan="3">Total ({totalItems} items)</td>
                <td className="px-4 py-3 text-right">{items.reduce((s, i) => s + parseFloat(i.current_stock || 0), 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">-</td>
                <td className="px-4 py-3 text-right">{formatCurrency(totalValue)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Movements Report */}
      {activeReport === 'movements' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <ArrowDownRight className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-green-600 text-sm font-medium">Stock In</p>
                  <p className="text-2xl font-bold text-green-800">+{movementSummary.in.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <ArrowUpRight className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-red-600 text-sm font-medium">Stock Out</p>
                  <p className="text-2xl font-bold text-red-800">-{movementSummary.out.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stock In Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-green-50">
              <h3 className="font-bold text-green-800 flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5" /> Stock In ({filteredMovements.filter(m => m.movement_type === 'in').length})
              </h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Quantity</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovements.filter(m => m.movement_type === 'in').map(m => (
                  <tr key={m.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{new Date(m.movement_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.InventoryItem?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{m.InventoryItem?.sku}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">+{parseFloat(m.quantity)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{m.reason || '-'}</td>
                  </tr>
                ))}
                {filteredMovements.filter(m => m.movement_type === 'in').length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-4 text-center text-gray-500">No stock in movements</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Stock Out Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b bg-red-50">
              <h3 className="font-bold text-red-800 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5" /> Stock Out ({filteredMovements.filter(m => m.movement_type === 'out').length})
              </h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Item</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Quantity</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovements.filter(m => m.movement_type === 'out').map(m => (
                  <tr key={m.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{new Date(m.movement_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.InventoryItem?.name || '-'}</div>
                      <div className="text-xs text-gray-500">{m.InventoryItem?.sku}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-red-600">-{parseFloat(m.quantity)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{m.reason || '-'}</td>
                  </tr>
                ))}
                {filteredMovements.filter(m => m.movement_type === 'out').length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-4 text-center text-gray-500">No stock out movements</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* POS Sales Report */}
      {activeReport === 'sales' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <ShoppingCart className="w-10 h-10 text-blue-600" />
                <div>
                  <p className="text-gray-600 text-sm">Total Sales</p>
                  <p className="text-2xl font-bold">{filteredPosTransactions.filter(t => t.payment_status === 'completed').length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-10 h-10 text-green-600" />
                <div>
                  <p className="text-gray-600 text-sm">Total Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(filteredPosTransactions.filter(t => t.payment_status === 'completed').reduce((s, t) => s + parseFloat(t.total_amount), 0))}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-4">
                <TrendingDown className="w-10 h-10 text-red-600" />
                <div>
                  <p className="text-gray-600 text-sm">Refunded</p>
                  <p className="text-2xl font-bold">{filteredPosTransactions.filter(t => t.payment_status === 'refunded').length}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-bold">Sales Transactions</h3>
              <p className="text-sm text-gray-600">{filteredPosTransactions.length} transactions</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Transaction #</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Items</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Customer</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Payment</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                  <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosTransactions.map(t => (
                  <tr key={t.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{t.transaction_number}</td>
                    <td className="px-4 py-3 text-sm">{new Date(t.transaction_date).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">
                      {(t.items || []).map(i => (
                        <div key={i.id} className="text-xs">{i.InventoryItem?.name || 'Item'} x{i.quantity}</div>
                      ))}
                    </td>
                    <td className="px-4 py-3">{t.customer_name || '-'}</td>
                    <td className="px-4 py-3 text-center capitalize">{t.payment_method}</td>
                    <td className="px-4 py-3 text-right font-bold">{formatCurrency(t.total_amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        t.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                        t.payment_status === 'refunded' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {t.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredPosTransactions.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No sales transactions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryReports;
